import React, { useState, useEffect, useMemo } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import useUserNameContext from "../../context/useUserNameContext";
import useSiteCodeContext from "../../context/useSiteCodeContext";
import { doesSiteBelongToSortCenter } from "../../utils/utils";
import useLocaleContext from "src/components/shared/context/useLocaleContext";
import { LEARN_THE_SAFETY_RULES_BULLETPOINTS } from "../../constants/messages";
import ApprovedLists from "../eligibilityCheck/approvedLists";
import { SubContent, generateKey } from "../..";
import useProcessPathPairsContext from "../../context/useProcessPathPairsContext";
import { separatePathsByEligibility } from "../../utils/utils";
import SiteInEligibleContent from "../siteInEligibleContent";
import ParseService from "src/components/shared/utils/parseService";
import { useBundle } from "@amzn/react-arb-tools";
import { HOMEPAGE_MESSAGE_BUNDLE_NAME } from "src/constants";
import { t } from "src/helpers";
import "./howItWorksText.scss";

const HowItWorksText = () => {
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const [approvedPathsInLanguage, setApprovedPathsInLanguage] = useState([]);
  const { language } = useLocaleContext();

  const userName = useUserNameContext();
  const siteCode = useSiteCodeContext();
  const { processPathPairs } = useProcessPathPairsContext();
  const isSiteEligible = processPathPairs.length !== 0;

  const isSortCenter = doesSiteBelongToSortCenter(siteCode);

  const approvedPaths = useMemo(
    () => separatePathsByEligibility(processPathPairs)[0],
    [processPathPairs]
  );

  useEffect(() => {
    ParseService.retrieveProcessPathsByLocale(approvedPaths, language)
      .then((res) => {
        setApprovedPathsInLanguage(res);
      })
      .catch((e) => {
        console.error(e.message);
      });
  }, [language, approvedPaths]);

  const getSafetyRules = () => {
    if (isSortCenter) {
      return LEARN_THE_SAFETY_RULES_BULLETPOINTS.filter(
        (rule) => rule !== "do_not_use_headphones"
      );
    }
    return LEARN_THE_SAFETY_RULES_BULLETPOINTS;
  };

  return (
    <Column spacing="500" spacingInset={"400 400 400 none"}>
      <Text type={"d50"} fontFamily={"bookerly"}>
        {t(bundle, "here_how_it_works")}
      </Text>

      <Column spacing="300">
        <Text fontFamily={"amazonEmber"} type={"h100"}>
          {t(bundle, "check_your_eligibility")}
        </Text>

        {isSiteEligible ? (
          <Column>
            <Column spacing="100">
              <Text fontFamily={"amazonEmber"} type={"b300"}>
                {t(bundle, "hello_user_name", { userName: <b>{userName}</b> })}
              </Text>
              {siteCode === "MOB5" ? (
                <Column>
                  <Text>
                    You’re located at <b>MOB5</b> which is <b>NOT</b> one of the
                    sites approved for headphone use in accordance with the
                    Approved Headphones Use Policy.{" "}
                    <b className="fontStyle">
                      However, if you are based at DMB3, you are at an approved
                      site for headphone use and can continue with the headphone
                      purchase process.
                    </b>
                  </Text>
                </Column>
              ) : (
                <Text fontFamily={"amazonEmber"} type={"b300"}>
                  {t(bundle, "you_are_located_at", {
                    siteCode: <b>{siteCode}</b>,
                  })}
                </Text>
              )}
            </Column>
            <Column>
              <Text fontFamily={"amazonEmber"} type={"b300"}>
                {t(bundle, "please_review_the_current")}{" "}
                {siteCode === "MOB5" && (
                  <b className="fontStyle">
                    Note: The below process paths apply to DMB3 ONLY:
                  </b>
                )}
              </Text>
            </Column>
            <Row spacing={"500"}>
              <Column></Column>
              <Text type="h200">{t(bundle, "approved_paths")}</Text>
            </Row>
            <Row spacing={"500"}>
              <Column></Column>
              <ApprovedLists
                processPaths={approvedPathsInLanguage}
              ></ApprovedLists>
            </Row>
          </Column>
        ) : (
          <SiteInEligibleContent></SiteInEligibleContent>
        )}
      </Column>
      <Column spacing="300">
        <SubContent
          title={t(bundle, "learn_the_safety_rules")}
          content={t(bundle, "these_rules_must_be")}
        ></SubContent>
        <Text fontFamily={"bookerly"} type={"b300"} tag="ul">
          {getSafetyRules().map((el, index) => (
            <li className="listStyle" key={generateKey(index)}>
              <div className="divStyle">{t(bundle, el)}</div>
            </li>
          ))}
        </Text>
      </Column>
      <SubContent
        title={t(bundle, "explore_your_options")}
        content={t(bundle, "there_are_two_headphones")}
      ></SubContent>
    </Column>
  );
};

export default HowItWorksText;
